// apps/web/src/pages/coletor/tinta-page.tsx — Fase 13, Plano 30 (EST-15, EST-14, D-01, D-02, D-05, D-06, D-28).
// Pesagem de tinta no tablet do almoxarifado: Lote (QR/código) -> Tinta -> Peso (teclado próprio) -> Registrar.
// Mesma tela, sem navegar; etapas concluídas colapsam em cartão com "Trocar". Sem fila offline nesta fase.
import { useCallback, useEffect, useReducer, useRef, useState, type FormEvent, type ReactElement } from 'react';
import { Loader2 } from 'lucide-react';
import type { IdentificacaoTinta, ModoPesagem, ResultadoPesagem, ResumoTintaLote, TintaCandidata } from '@pieltec/contracts';
import { QrScanner } from '../../components/coletor/qr-scanner';
import { ColetorBarraIdentidade } from '../../components/coletor/coletor-identidade';
import { TecladoNumerico } from '../../components/coletor/teclado-numerico';
import { attachUsbReader } from '../../components/coletor/usb-reader';
import { apiMutate } from '../../lib/csrf-fetch';
import {
  MENSAGEM_CODIGO_NAO_ENCONTRADO,
  MOTIVOS_PERDA_TINTA,
  ROTULOS_BOTAO_FINAL,
  ROTULOS_MODO_PESAGEM,
  ROTULOS_MOTIVO_PERDA,
  chaveDaTentativa,
  estadoInicialPesagem,
  excedenteDevolucao,
  mensagemErro,
  modoDesabilitado,
  montarPayloadPesagem,
  podeDevolverSobra,
  podeRegistrar,
  reduzirPesagem,
  textoSucesso,
} from '../../lib/estoque/pesagem';
import { exibirPeso, validarPeso } from '../../lib/estoque/teclado-peso';

const MODOS: readonly ModoPesagem[] = ['SAIDA', 'DEVOLUCAO', 'PERDA'];

const MENSAGEM_FALHA_IDENTIFICAR = 'Não foi possível identificar o código. Tente de novo.';
const ATRASO_BUSCA_MS = 250;

const CARTAO = 'rounded-2xl border border-slate-800 bg-slate-900 p-4';
const BOTAO_GRANDE =
  'min-h-[64px] w-full rounded-xl border border-slate-700 bg-slate-800 px-4 text-base font-semibold text-slate-100 touch-manipulation ' +
  'hover:bg-slate-700 active:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:opacity-50';
const BOTAO_PRIMARIO =
  'min-h-[64px] w-full rounded-xl bg-emerald-700 px-4 text-base font-semibold text-white touch-manipulation ' +
  'hover:bg-emerald-600 active:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-50';
const ROTULO = 'text-xs font-semibold uppercase tracking-wider text-slate-400';

function nomeDaCor(tinta: Pick<TintaCandidata, 'corCodigo'>): string {
  return tinta.corCodigo ?? 'sem cor';
}

function formatarKg(valor: number): string {
  return valor.toLocaleString('pt-BR', { maximumFractionDigits: 4 });
}

async function buscarJson<T>(caminho: string): Promise<{ status: number; corpo: T | null }> {
  const resposta = await fetch(caminho, { credentials: 'include' });
  let corpo: T | null = null;
  try {
    corpo = (await resposta.json()) as T;
  } catch {
    corpo = null;
  }
  return { status: resposta.status, corpo };
}

export function TintaColetorPage(): ReactElement {
  const [estado, despachar] = useReducer(reduzirPesagem, undefined, () => estadoInicialPesagem());
  const [cameraAberta, setCameraAberta] = useState(false);
  const [digitando, setDigitando] = useState(false);
  const [codigoDigitado, setCodigoDigitado] = useState('');
  const [identificando, setIdentificando] = useState(false);
  const [erroLote, setErroLote] = useState<string | null>(null);
  const [busca, setBusca] = useState('');
  const [tintas, setTintas] = useState<TintaCandidata[] | null>(null);
  const enviandoRef = useRef(false);
  const sequenciaIdentificar = useRef(0);

  const identificar = useCallback(async (codigoLido: string): Promise<void> => {
    const codigo = codigoLido.trim();
    if (codigo === '') return;
    const sequencia = (sequenciaIdentificar.current += 1);
    setCameraAberta(false);
    setIdentificando(true);
    setErroLote(null);
    try {
      const { status, corpo } = await buscarJson<IdentificacaoTinta>(`/api/v1/estoque/tinta/identificar/${encodeURIComponent(codigo)}`);
      if (sequencia !== sequenciaIdentificar.current) return;
      if (status === 404) {
        setErroLote(MENSAGEM_CODIGO_NAO_ENCONTRADO);
      } else if (status < 200 || status >= 300 || corpo === null) {
        setErroLote(MENSAGEM_FALHA_IDENTIFICAR);
      } else {
        setBusca('');
        setTintas(null);
        setDigitando(false);
        setCodigoDigitado('');
        despachar({ tipo: 'IDENTIFICADO', identificacao: corpo });
      }
    } catch {
      if (sequencia === sequenciaIdentificar.current) setErroLote(MENSAGEM_FALHA_IDENTIFICAR);
    } finally {
      if (sequencia === sequenciaIdentificar.current) setIdentificando(false);
    }
  }, []);

  const aoLerCamera = useCallback((texto: string) => void identificar(texto), [identificar]);
  const fecharCamera = useCallback(() => setCameraAberta(false), []);

  // Leitor USB ativo em paralelo à câmera, enquanto a etapa do lote está aberta (mesmo comportamento de leitura-page).
  useEffect(() => {
    if (estado.etapa !== 'LOTE') return undefined;
    return attachUsbReader({ onScan: (texto) => void identificar(texto) });
  }, [estado.etapa, identificar]);

  // Lista de tintas da etapa 2: busca no servidor (com atraso) e, no alto giro, só as tintas com a flag.
  const apenasAltoGiro = estado.origem === 'ALTO_GIRO';
  useEffect(() => {
    if (estado.etapa !== 'TINTA') return undefined;
    let ativo = true;
    const espera = setTimeout(() => {
      const consulta = new URLSearchParams();
      if (busca.trim() !== '') consulta.set('busca', busca.trim());
      if (apenasAltoGiro) consulta.set('apenasAltoGiro', 'true');
      void buscarJson<TintaCandidata[]>(`/api/v1/estoque/tinta/tintas?${consulta.toString()}`)
        .then(({ status, corpo }) => {
          if (ativo) setTintas(status >= 200 && status < 300 && Array.isArray(corpo) ? corpo : []);
        })
        .catch(() => {
          if (ativo) setTintas([]);
        });
    }, ATRASO_BUSCA_MS);
    return () => {
      ativo = false;
      clearTimeout(espera);
    };
  }, [estado.etapa, busca, apenasAltoGiro]);

  // Devolução: consumo da tinta no lote ("Neste lote: saiu {S} kg · já devolvido {D} kg").
  const loteId = estado.identificacao?.lote.id ?? null;
  const materialId = estado.tinta?.materialId ?? null;
  const tintaAltoGiro = estado.tinta?.altoGiro === true;
  useEffect(() => {
    if (estado.modo !== 'DEVOLUCAO' || estado.etapa !== 'PESO' || loteId === null || materialId === null || tintaAltoGiro) return undefined;
    let ativo = true;
    void buscarJson<ResumoTintaLote>(`/api/v1/estoque/tinta/lotes/${encodeURIComponent(loteId)}/resumo?materialId=${encodeURIComponent(materialId)}`)
      .then(({ status, corpo }) => {
        if (ativo) despachar({ tipo: 'RESUMO', resumo: status >= 200 && status < 300 ? corpo : null });
      })
      .catch(() => {
        if (ativo) despachar({ tipo: 'RESUMO', resumo: null });
      });
    return () => {
      ativo = false;
    };
  }, [estado.modo, estado.etapa, loteId, materialId, tintaAltoGiro]);

  async function registrar(): Promise<void> {
    if (enviandoRef.current || !podeRegistrar(estado)) return;
    const chave = chaveDaTentativa(estado, () => crypto.randomUUID());
    const payload = montarPayloadPesagem(estado, chave);
    if (payload === null || estado.tinta === null) return;
    enviandoRef.current = true;
    despachar({ tipo: 'ENVIANDO', chave });
    try {
      const resposta = await apiMutate('/api/v1/estoque/tinta/movimentos', { method: 'POST', body: payload });
      if (resposta.ok) {
        const resultado = (await resposta.json().catch(() => null)) as ResultadoPesagem | null;
        despachar({
          tipo: 'SUCESSO',
          sucesso: {
            modo: estado.modo,
            pesoTexto: estado.pesoTexto,
            tintaCodigo: estado.tinta.codigo,
            lote: estado.identificacao?.lote.loteInternoPieltec ?? null,
            motivo: estado.modo === 'PERDA' && estado.motivoPerda ? ROTULOS_MOTIVO_PERDA[estado.motivoPerda] : null,
            repetido: resultado?.repetido ?? resposta.status === 200,
            podeDevolverSobra: podeDevolverSobra(estado),
          },
        });
        return;
      }
      const corpo: unknown = await resposta.json().catch(() => null);
      const erro = mensagemErro({ tipo: 'HTTP', status: resposta.status, corpo });
      if (erro.excedente) {
        // A devolução passou do saído: o alerta amber pede a justificativa (avisa, não trava).
        despachar({ tipo: 'EXIGE_JUSTIFICATIVA', devolucaoKg: erro.excedente.devolucaoKg, saidaKg: erro.excedente.saidaKg });
      } else {
        despachar({ tipo: 'FALHA', mensagem: erro.mensagem, manterChave: erro.manterChave });
      }
    } catch {
      const erro = mensagemErro({ tipo: 'REDE' });
      despachar({ tipo: 'FALHA', mensagem: erro.mensagem, manterChave: erro.manterChave });
    } finally {
      enviandoRef.current = false;
    }
  }

  function enviarCodigo(evento: FormEvent): void {
    evento.preventDefault();
    void identificar(codigoDigitado);
  }

  // Lista exibida: candidatas da cor do item primeiro (quando o lote foi lido), depois as demais da busca.
  const candidatasDoLote = estado.identificacao?.tintasCandidatas ?? [];
  const termo = busca.trim().toLowerCase();
  const candidatasFiltradas = candidatasDoLote.filter(
    (tinta) => termo === '' || `${tinta.codigo} ${tinta.nome} ${tinta.corCodigo ?? ''}`.toLowerCase().includes(termo),
  );
  const idsDasCandidatas = new Set(candidatasFiltradas.map((tinta) => tinta.materialId));
  const listaDeTintas = [...candidatasFiltradas, ...(tintas ?? []).filter((tinta) => !idsDasCandidatas.has(tinta.materialId))];

  const erroPeso = estado.pesoTexto === '' ? null : validarPeso(estado.pesoTexto);
  const identificacao = estado.identificacao;
  const emPeso = estado.etapa === 'PESO';
  const excedente = excedenteDevolucao(estado);
  const altoGiroEmDevolucao = estado.modo === 'DEVOLUCAO' && estado.tinta?.altoGiro === true;
  const tintaDeAltoGiro = estado.origem === 'ALTO_GIRO' || estado.tinta?.altoGiro === true;
  const avisoAltoGiroSemDevolucao = (
    <p className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-base text-amber-300" role="note">
      Esta cor não retorna ao estoque. Registre só a saída.
    </p>
  );
  const nenhumaTintaDeAltoGiro = estado.origem === 'ALTO_GIRO' && busca.trim() === '' && tintas !== null && listaDeTintas.length === 0;

  return (
    <div className="min-h-screen select-none bg-slate-950 text-slate-100" data-theme="dark-premium" data-density="touch">
      <div className={`mx-auto flex w-full max-w-4xl flex-col gap-4 p-4 ${emPeso ? 'pb-36' : 'pb-8'}`}>
        <ColetorBarraIdentidade />
        <header className="flex flex-col gap-1">
          <p className={ROTULO}>Almoxarifado</p>
          <h1 className="text-[28px] font-semibold leading-tight text-white">Pesagem de tinta</h1>
        </header>

        {estado.etapa !== 'SUCESSO' && (
          <div role="radiogroup" aria-label="Modo da pesagem" className="grid grid-cols-3 gap-2">
            {MODOS.map((modo) => {
              const desabilitado = modoDesabilitado(estado, modo) || estado.enviando;
              const ativo = estado.modo === modo;
              return (
                <button
                  key={modo}
                  type="button"
                  role="radio"
                  aria-checked={ativo}
                  disabled={desabilitado}
                  onClick={() => despachar({ tipo: 'MODO', modo })}
                  className={`min-h-[64px] rounded-xl border px-2 text-base font-semibold touch-manipulation focus:outline-none focus:ring-2 focus:ring-emerald-400 disabled:opacity-40 ${
                    ativo ? 'border-emerald-500 bg-emerald-700 text-white' : 'border-slate-700 bg-slate-900 text-slate-200 hover:bg-slate-800'
                  }`}
                >
                  {ROTULOS_MODO_PESAGEM[modo]}
                </button>
              );
            })}
          </div>
        )}

        {estado.etapa === 'SUCESSO' && estado.sucesso && (
          <section className="flex flex-col gap-3 rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-4" role="status">
            <p className="text-xl font-semibold text-emerald-300">{textoSucesso(estado.sucesso)}</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <button type="button" className={BOTAO_PRIMARIO} onClick={() => despachar({ tipo: 'NOVA_SAIDA' })}>
                Nova saída
              </button>
              {estado.sucesso.podeDevolverSobra && (
                <button type="button" className={BOTAO_GRANDE} onClick={() => despachar({ tipo: 'DEVOLVER_SOBRA' })}>
                  Devolver sobra deste lote
                </button>
              )}
              <button type="button" className={BOTAO_GRANDE} onClick={() => despachar({ tipo: 'CONCLUIR' })}>
                Concluir pesagem
              </button>
            </div>
          </section>
        )}

        {/* Etapa 1 — lote */}
        {estado.etapa !== 'SUCESSO' && (
          <section className={CARTAO} aria-label="Lote">
            {estado.etapa === 'LOTE' ? (
              <div className="flex flex-col gap-3">
                <p className={ROTULO}>{estado.modo === 'PERDA' ? 'Lote ou cor (opcional)' : 'Lote'}</p>
                <button type="button" className={BOTAO_PRIMARIO} onClick={() => setCameraAberta(true)}>
                  Ler QR do lote/OP
                </button>
                <p className="text-xs text-slate-400">Aproxime o leitor USB ou use a câmera do tablet.</p>
                {estado.modo === 'SAIDA' && (
                  <button type="button" className={BOTAO_GRANDE} onClick={() => despachar({ tipo: 'SEM_LOTE', origem: 'ALTO_GIRO' })}>
                    Tinta de alto giro (sem lote)
                  </button>
                )}
                {estado.modo === 'PERDA' && (
                  <button type="button" className={BOTAO_GRANDE} onClick={() => despachar({ tipo: 'SEM_LOTE', origem: 'SEM_LOTE' })}>
                    Continuar sem lote
                  </button>
                )}
                {digitando ? (
                  <form onSubmit={enviarCodigo} className="flex flex-col gap-2">
                    <label htmlFor="codigo-lote" className={ROTULO}>
                      Código do lote ou da OP
                    </label>
                    <input
                      id="codigo-lote"
                      value={codigoDigitado}
                      onChange={(evento) => setCodigoDigitado(evento.target.value)}
                      autoFocus
                      autoComplete="off"
                      className="min-h-[64px] w-full rounded-xl border border-slate-700 bg-slate-950 px-4 text-base text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                    />
                    <button type="submit" className={BOTAO_GRANDE} disabled={identificando || codigoDigitado.trim() === ''}>
                      Identificar
                    </button>
                  </form>
                ) : (
                  <button type="button" className={BOTAO_GRANDE} onClick={() => setDigitando(true)}>
                    Digitar o código
                  </button>
                )}
                {identificando && (
                  <p className="text-base text-blue-300" role="status">
                    Identificando…
                  </p>
                )}
                {erroLote && (
                  <p className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-3 text-base text-rose-300" role="alert">
                    {erroLote}
                  </p>
                )}
              </div>
            ) : identificacao ? (
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xl font-semibold text-white">
                    Lote {identificacao.lote.loteInternoPieltec}
                    {identificacao.op ? ` · OP ${identificacao.op.numero}` : ''}
                  </p>
                  <p className="truncate text-base text-slate-300">
                    {identificacao.clienteNome ?? 'Sem cliente'} · {identificacao.item.nome} · cor {identificacao.item.corCodigo ?? 'sem cor'}
                  </p>
                </div>
                <button type="button" className={`${BOTAO_GRANDE} w-auto shrink-0`} disabled={estado.enviando} onClick={() => despachar({ tipo: 'TROCAR_LOTE' })}>
                  Trocar lote
                </button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <p className="text-xl font-semibold text-white">{estado.origem === 'ALTO_GIRO' ? 'Tinta de alto giro (sem lote)' : 'Sem lote'}</p>
                <button type="button" className={`${BOTAO_GRANDE} w-auto shrink-0`} disabled={estado.enviando} onClick={() => despachar({ tipo: 'TROCAR_LOTE' })}>
                  Trocar lote
                </button>
              </div>
            )}
          </section>
        )}

        {/* Etapa 2 — tinta */}
        {(estado.etapa === 'TINTA' || estado.etapa === 'PESO') && (
          <section className={CARTAO} aria-label="Tinta">
            {estado.etapa === 'TINTA' ? (
              <div className="flex flex-col gap-3">
                <label htmlFor="busca-tinta" className={ROTULO}>
                  Tinta
                </label>
                <input
                  id="busca-tinta"
                  value={busca}
                  onChange={(evento) => setBusca(evento.target.value)}
                  placeholder="Buscar por código ou cor"
                  autoComplete="off"
                  className="min-h-[64px] w-full rounded-xl border border-slate-700 bg-slate-950 px-4 text-base text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                />
                {tintaDeAltoGiro && (
                  <p className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-base text-amber-300" role="note">
                    {estado.tinta?.altoGiro ? nomeDaCor(estado.tinta) : 'Alto giro'}: consumo rateado por área pintada, por dia. Não é preciso informar lote.
                  </p>
                )}
                {altoGiroEmDevolucao && avisoAltoGiroSemDevolucao}
                {tintas === null && listaDeTintas.length === 0 ? (
                  <p className="text-base text-slate-400">Carregando…</p>
                ) : nenhumaTintaDeAltoGiro ? (
                  <p className="text-base text-slate-300">Nenhuma tinta de alto giro cadastrada. Peça ao PCP para marcar a flag no cadastro.</p>
                ) : listaDeTintas.length === 0 ? (
                  <div className="flex flex-col gap-2">
                    <p className="text-base text-slate-300">Nenhuma tinta encontrada.</p>
                    {busca.trim() !== '' && (
                      <button type="button" className={BOTAO_GRANDE} onClick={() => setBusca('')}>
                        Limpar a busca
                      </button>
                    )}
                  </div>
                ) : (
                  <ul className="flex flex-col gap-2">
                    {listaDeTintas.map((tinta) => (
                      <li key={tinta.materialId}>
                        <button
                          type="button"
                          aria-pressed={estado.tinta?.materialId === tinta.materialId}
                          className={`${BOTAO_GRANDE} flex items-center justify-between gap-3 text-left ${
                            estado.tinta?.materialId === tinta.materialId ? 'border-emerald-500 bg-emerald-700/20' : ''
                          }`}
                          onClick={() => despachar({ tipo: 'TINTA', tinta })}
                        >
                          <span>
                            {tinta.codigo} · {nomeDaCor(tinta)}
                          </span>
                          <span className="shrink-0 font-normal text-slate-300">{formatarKg(tinta.saldoKg)} kg</span>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {estado.tinta && (
                  <button type="button" className={BOTAO_PRIMARIO} onClick={() => despachar({ tipo: 'CONTINUAR_COM_TINTA' })}>
                    Continuar com {estado.tinta.codigo}
                  </button>
                )}
              </div>
            ) : estado.tinta ? (
              <div className="flex items-center justify-between gap-3">
                <p className="min-w-0 truncate text-xl font-semibold text-white">
                  {estado.tinta.codigo} · {nomeDaCor(estado.tinta)}
                </p>
                <button type="button" className={`${BOTAO_GRANDE} w-auto shrink-0`} disabled={estado.enviando} onClick={() => despachar({ tipo: 'TROCAR_TINTA' })}>
                  Trocar
                </button>
              </div>
            ) : null}
          </section>
        )}

        {/* Etapa 3 — peso */}
        {emPeso && estado.modo === 'PERDA' && (
          <section className={CARTAO} aria-label="Motivo da perda">
            <div className="flex flex-col gap-3">
              <p className={ROTULO} id="rotulo-motivo-perda">
                Motivo da perda
              </p>
              <div role="radiogroup" aria-labelledby="rotulo-motivo-perda" className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {MOTIVOS_PERDA_TINTA.map((motivo) => {
                  const ativo = estado.motivoPerda === motivo;
                  return (
                    <button
                      key={motivo}
                      type="button"
                      role="radio"
                      aria-checked={ativo}
                      disabled={estado.enviando}
                      onClick={() => despachar({ tipo: 'MOTIVO', motivo })}
                      className={`min-h-[64px] rounded-xl border px-2 text-base font-semibold touch-manipulation focus:outline-none focus:ring-2 focus:ring-emerald-400 ${
                        ativo ? 'border-emerald-500 bg-emerald-700 text-white' : 'border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700'
                      }`}
                    >
                      {ROTULOS_MOTIVO_PERDA[motivo]}
                    </button>
                  );
                })}
              </div>
              <label htmlFor="detalhes-perda" className={ROTULO}>
                {estado.motivoPerda === 'OUTRO' ? 'Detalhes (obrigatório)' : 'Detalhes'}
              </label>
              <input
                id="detalhes-perda"
                value={estado.detalhes}
                disabled={estado.enviando}
                onChange={(evento) => despachar({ tipo: 'DETALHES', texto: evento.target.value })}
                maxLength={500}
                autoComplete="off"
                className="min-h-[64px] w-full rounded-xl border border-slate-700 bg-slate-950 px-4 text-base text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-400"
              />
            </div>
          </section>
        )}

        {emPeso && estado.modo === 'DEVOLUCAO' && (
          <section className={CARTAO} aria-label="Devolução">
            <div className="flex flex-col gap-3">
              {altoGiroEmDevolucao ? (
                avisoAltoGiroSemDevolucao
              ) : (
                <>
                  {estado.resumo && (
                    <p className="text-base text-slate-200">
                      Neste lote: saiu {formatarKg(estado.resumo.saidaKg)} kg · já devolvido {formatarKg(estado.resumo.devolvidoKg)} kg
                    </p>
                  )}
                  {excedente && (
                    <div className="flex flex-col gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3" role="alert">
                      <p className="text-base text-amber-300">
                        A devolução ({formatarKg(excedente.devolucaoKg)} kg) é maior que o registrado como saída deste lote ({formatarKg(excedente.saidaKg)} kg).
                        Informe a justificativa para continuar.
                      </p>
                      <label htmlFor="justificativa-devolucao" className={ROTULO}>
                        Justificativa (obrigatória)
                      </label>
                      <input
                        id="justificativa-devolucao"
                        value={estado.justificativa}
                        disabled={estado.enviando}
                        onChange={(evento) => despachar({ tipo: 'JUSTIFICATIVA', texto: evento.target.value })}
                        maxLength={500}
                        autoComplete="off"
                        className="min-h-[64px] w-full rounded-xl border border-slate-700 bg-slate-950 px-4 text-base text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-400"
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </section>
        )}

        {emPeso && (
          <section className={CARTAO} aria-label="Peso">
            <div className="flex flex-col gap-3">
              <p className={ROTULO}>Peso líquido (kg)</p>
              <div className="rounded-xl border border-slate-700 bg-slate-950 p-4 text-right">
                <div data-fonte-peso="manual" aria-live="polite" className="whitespace-nowrap text-[28px] font-semibold leading-tight tabular-nums text-white">
                  {exibirPeso(estado.pesoTexto)} kg
                </div>
              </div>
              <p className="text-xs text-slate-400">Digite o peso da tinta, sem a embalagem.</p>
              {erroPeso && estado.pesoTexto !== '' && (
                <p className="text-base text-amber-300" role="status">
                  {erroPeso}
                </p>
              )}
              <TecladoNumerico
                onTecla={(tecla) => despachar({ tipo: 'TECLA', tecla })}
                onConfirmar={() => void registrar()}
                desabilitado={estado.enviando}
              />
            </div>
          </section>
        )}

        {estado.erro && (
          <p className="rounded-xl border border-rose-500/40 bg-rose-500/10 p-3 text-base text-rose-300" role="alert">
            {estado.erro}
          </p>
        )}
      </div>

      {emPeso && (
        <div className="fixed inset-x-0 bottom-0 border-t border-slate-800 bg-slate-950/95 p-4">
          <div className="mx-auto w-full max-w-4xl">
            <button type="button" className={BOTAO_PRIMARIO} disabled={!podeRegistrar(estado)} onClick={() => void registrar()}>
              {estado.enviando ? (
                <span className="inline-flex items-center justify-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                  Registrando…
                </span>
              ) : (
                ROTULOS_BOTAO_FINAL[estado.modo]
              )}
            </button>
          </div>
        </div>
      )}

      {cameraAberta && <QrScanner onScan={aoLerCamera} onClose={fecharCamera} />}
    </div>
  );
}
